/**
 * Product Basic Info Types
 *
 * Zod schemas and types for the Overview step of the Add Product form.
 * Sections: Basic Information, Description, Allergens, Verification.
 */
import { z } from 'zod';

// -- Helpers ----------------------------------------------------
export const optionalDecimal = z.string().optional();
export const optionalString = z.string().optional();

/** Unit of measure for the Expiry After Mixing / Expiry After Opening fields. */
export const ExpiryUnitSchema = z.enum(['hours', 'days']);
export type ExpiryUnit = z.infer<typeof ExpiryUnitSchema>;

/** Accepts only positive numeric/decimal values (e.g. '0.67', '120', '3.5') */
const optionalPositiveDecimal = z
  .string()
  .optional()
  .refine(
    (val) => !val || /^\d*\.?\d+$/.test(val.trim()),
    { message: 'Must be a positive numeric value' },
  );

// -- Basic Information ------------------------------------------

const BaseBasicInformationSchema = z.object({
  sku: z.string().min(1, 'SKU is required'),
  tms_product_id: optionalString,
  brand_name: z.string().min(1, 'Brand Name is required'),
  manufacturer: z.string().min(1, 'Manufacturer is required'),
  type_of_product: optionalString,
  form: z.string().min(1, 'Form is required'),
  measurement_metric: optionalString,
  calories_for_liquids: optionalPositiveDecimal,
  calories_for_pwds: optionalPositiveDecimal,
  flavor: optionalString,
  product_status: optionalString,
  hcpcs: optionalString,
  ndc_format_code: optionalString,
  age_for_use: optionalString,
  region_for_use: optionalString,
  date_verified: optionalString,
});

export const BasicInformationSchema = BaseBasicInformationSchema.superRefine((data, ctx) => {
  const metric = (data.measurement_metric ?? '').toLowerCase();
  const isGramMetric = metric.includes('gram');
  if (isGramMetric) {
    if (!data.calories_for_pwds || data.calories_for_pwds.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Calories for PWDs is required',
        path: ['calories_for_pwds'],
      });
    }
  } else if (metric.includes('ml')) {
    if (!data.calories_for_liquids || data.calories_for_liquids.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Calories for Liquids is required',
        path: ['calories_for_liquids'],
      });
    }
  }
});

// -- Description ------------------------------------------------

export const DescriptionSchema = z.object({
  product_description: optionalString,
  formula_classification: z.array(z.string()).optional().default([]),
  parenteral_category: optionalString,
  substitutions: optionalString,
});

// -- Allergens --------------------------------------------------

export const AllergensSchema = z.object({
  egg: z.boolean().default(false),
  fish: z.boolean().default(false),
  lactose: z.boolean().default(false),
  latex: z.boolean().default(false),
  milk: z.boolean().default(false),
  peanut: z.boolean().default(false),
  sesame: z.boolean().default(false),
  shellfish: z.boolean().default(false),
  soy: z.boolean().default(false),
  tree_nuts: z.boolean().default(false),
  wheat: z.boolean().default(false),
  // Europe Allergens
  contains_allergen_crustaceans: z.boolean().default(false),
  contains_allergen_molluscs: z.boolean().default(false),
  contains_allergen_celery: z.boolean().default(false),
  contains_allergen_mustard: z.boolean().default(false),
  contains_allergen_sulphur_dioxide: z.boolean().default(false),
  contains_allergen_sulphites: z.boolean().default(false),
  contains_allergen_lupin: z.boolean().default(false),
});

// -- Verification -----------------------------------------------

export const VerificationSchema = z.object({
  verification_source_url: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z
      .string()
      .url('Please enter a valid URL (e.g. https://example.com)')
      .optional(),
  ),
});

// -- Expiration ---------------------------------------------------
// Split out of Packaging's former "Storage & Expiration" section — Expiration
// now lives on the Overview step, Storage stays on the Packaging step.

export const ExpirationSchema = z.object({
  expiration_after_mixing: optionalDecimal,
  expiration_after_mixing_unit: ExpiryUnitSchema.optional(),
  expiration_after_opening: optionalDecimal,
  expiration_after_opening_unit: ExpiryUnitSchema.optional(),
});

// -- Combined Overview step -------------------------------------

export const OverviewSchema = z.object({
  basic_information: BasicInformationSchema,
  description: DescriptionSchema,
  allergens: AllergensSchema,
  verification: VerificationSchema,
  expiration: ExpirationSchema,
});

export type OverviewFormValues = z.infer<typeof OverviewSchema>;
